<template>
  <!-- Pinned by moving into the app bar's extension, never by going sticky here:
       see `breadcrumbSpine.ts` for why the sticky version could not coexist with
       everything else anchored under the app bar. -->
  <Teleport :to="`#${BREADCRUMB_EXTENSION_ID}`" :disabled="!isPinned">
    <!-- The trail belongs to the page, so it keeps the page's theme even while it
         lives inside the app bar, which declares `theme="dark"` over its own
         subtree. Without this the pinned strip paints the dark theme's near-black
         background under the page's dark-blue crumb text (measured: background
         resolved to rgb(18,18,18) against link text rgb(31,85,146)).
         `with-background` is what makes this render an element at all - without
         it VThemeProvider only provides the theme to Vuetify components and
         re-declares no CSS variables, so the strip stayed dark. -->
    <v-theme-provider v-if="items.length" theme="light" with-background>
      <div
        ref="spine"
        class="app-breadcrumbs-spine"
        :class="{ 'app-breadcrumbs-spine--pinned': isPinned }"
        data-testid="app-breadcrumbs-spine"
      >
        <DashboardBreadcrumbs :items="items" />
      </div>
    </v-theme-provider>
  </Teleport>
</template>

<script setup lang="ts">
// The single breadcrumb bar for the whole app ([NAV-BREADCRUMB-ONE-BAR]). Rendered
// once in MainLayout so every route gets a consistent trail. The base trail is
// resolved from the shared `navModel`; deep-leaf routes declare typed
// `route.meta.breadcrumb` metadata. Views whose leaf label depends on fetched
// data publish it via `useBreadcrumbTail`. Renders nothing when a route opts out
// (auth screens, print views, public token pages).
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useDisplay } from "vuetify";
import DashboardBreadcrumbs, { type DashboardBreadcrumbItem } from "./DashboardBreadcrumbs.vue";
import { useNavModel } from "@/navigation/useNavModel";
import { resolveBreadcrumbTrail, type BreadcrumbMeta } from "@/navigation/breadcrumbs";
import { getDynamicBreadcrumbTail } from "@/navigation/breadcrumbTail";
import { BREADCRUMB_EXTENSION_ID, publishBreadcrumbSpineHeight } from "@/navigation/breadcrumbSpine";
import { WORKSPACE_DRAWER_BREAKPOINT } from "@/composables/useWorkspaceDisplay";
import { useAuthStore } from "@/store/auth";

const route = useRoute();
const display = useDisplay();
const { model } = useNavModel();
const authStore = useAuthStore();

const HOME_PATH = "/home";

const items = computed<DashboardBreadcrumbItem[]>(() => {
  const meta = route.meta.breadcrumb;

  // A published dynamic tail replaces the static meta tail over the same base.
  const dynamicTail = getDynamicBreadcrumbTail(route.path);
  const effectiveMeta: BreadcrumbMeta | undefined = dynamicTail ? { ...meta, tail: () => dynamicTail } : meta;

  const trail = resolveBreadcrumbTrail(model.value, route, effectiveMeta);
  if (!trail) return [];

  const crumbs: DashboardBreadcrumbItem[] = trail.map((crumb) => ({ label: crumb.label, to: crumb.to }));

  // Root every bar at Home for authenticated users, so Home is a consistent,
  // always-present anchor (it was dropped from the sidebar top). Skip on the
  // home page itself, where the resolved trail already IS the Home crumb. Use
  // the nav model's Home node so the personal-dashboard query is preserved.
  if (authStore.isAuthenticated && route.path !== HOME_PATH) {
    const homeNode = model.value.find((node) => node.id === "home");
    crumbs.unshift({ label: homeNode?.label ?? "Home", to: homeNode?.to ?? HOME_PATH });
  }

  return crumbs;
});

// Pinned above WORKSPACE_DRAWER_BREAKPOINT by teleporting into the app bar's
// extension, so the room it takes is part of `--v-layout-top` rather than
// something every other pinned element has to subtract for itself. Vuetify sizes
// that extension from a number, and the trail wraps to a second line when it is
// long, so measure the rendered element rather than assuming a constant.
const isPinned = computed(() => (display.width?.value ?? 0) >= WORKSPACE_DRAWER_BREAKPOINT);
const spine = ref<HTMLElement | null>(null);
let spineObserver: ResizeObserver | null = null;

watch(
  spine,
  (element) => {
    spineObserver?.disconnect();
    spineObserver = null;

    if (!element) {
      publishBreadcrumbSpineHeight(0);
      return;
    }

    // Border-box height: the spine is `display: flow-root`, so the trail's own
    // bottom margin is inside it and the app bar reserves room for all of it.
    publishBreadcrumbSpineHeight(element.getBoundingClientRect().height);
    if (typeof ResizeObserver === "undefined") return;
    spineObserver = new ResizeObserver(([entry]) =>
      publishBreadcrumbSpineHeight(entry.target.getBoundingClientRect().height),
    );
    spineObserver.observe(element);
  },
  { flush: "post" },
);

onBeforeUnmount(() => {
  spineObserver?.disconnect();
  spineObserver = null;
  publishBreadcrumbSpineHeight(0);
});
</script>

<style scoped>
/* Full-width breadcrumb spine ([NAV-BREADCRUMB-ONE-BAR]). The inner nav carries its own
   margin-bottom; add a horizontal gutter so the crumbs are not flush against the
   viewport edge. `flow-root` keeps that margin inside the spine's own box, so the
   height the app bar reserves covers all of it. */
.app-breadcrumbs-spine {
  display: flow-root;
  padding-block-start: 6px;
  padding-inline: clamp(16px, 4vw, 32px);
}

/* The theme provider above paints the page background; the trail only adds the
   hairline that separates it from the page when it is pinned. */
.app-breadcrumbs-spine--pinned {
  border-block-end: var(--th-border-soft);
}
</style>
